import React, { Component } from 'react';
import { Row, Col, Card, Typography, Breadcrumb, Space, Button, Spin } from 'antd';
import {
  DashboardOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  RollbackOutlined,
  SplitCellsOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
  FileSearchOutlined,
  ReloadOutlined,
  HomeOutlined
} from '@ant-design/icons';
import StatusCard from './StatusCard';
import OrderListModal from './OrderListModal';
import './OrderDashboard.css';

const { Title, Text } = Typography;


class OrderDashboard extends Component {
  constructor(props) {
    super(props);
    this.unsubscribers = [];
    this.cachedOrdersDocs = [];
    this.cachedSdsDocs = [];

    this.state = {
      loading: false,

      // Initialize counts dynamically from props or defaults
      counts: this.calculatePropsCounts(props),

      // Modal state
      listModalVisible: false,
      selectedCard: null,
      filteredOrders: []
    };
  }

  componentDidMount() {
    this.setupRealtimeListeners();
  }

  componentDidUpdate(prevProps) {
    const prevOrgId = prevProps.org?.id || "";
    const nextOrgId = this.props.org?.id || "";
    const prevUserOrgId = prevProps.userOrg?.id || "";
    const nextUserOrgId = this.props.userOrg?.id || "";

    if (
      prevOrgId !== nextOrgId ||
      prevUserOrgId !== nextUserOrgId ||
      prevProps.unreadOrderWithOrg !== this.props.unreadOrderWithOrg ||
      prevProps.sdsWaitingCountWithOrg !== this.props.sdsWaitingCountWithOrg
    ) {
      this.setState({ counts: this.calculatePropsCounts(this.props) });
      this.setupRealtimeListeners();
    }
  }

  componentWillUnmount() {
    this.clearListeners();
  }

  // Calculate badge counts matching App.js & P4Header menu badges 100%
  calculatePropsCounts = (props) => {
    const orgId = props.org?.id;
    let unread = 0;
    let notShipped = 0;
    let shipped = 0;
    let cancelPending = 0;
    let returnPending = 0;
    let splitPending = 0;
    let sdsRequest = 0;
    let sdsNormal = 0;
    let sdsExpired = 0;

    if (orgId) {
      unread = (props.unreadOrderWithOrg && props.unreadOrderWithOrg[orgId]) || 0;
      notShipped = (props.notShippedOrderWithOrg && props.notShippedOrderWithOrg[orgId]) || 0;
      cancelPending = (props.cancelOrderWithOrg && props.cancelOrderWithOrg[orgId]) || 0;
      returnPending = (props.returnOrderWithOrg && props.returnOrderWithOrg[orgId]) || 0;
      sdsRequest = (props.sdsWaitingCountWithOrg && props.sdsWaitingCountWithOrg[orgId]) || 0;
      sdsNormal = (props.sdsNormalCountWithOrg && props.sdsNormalCountWithOrg[orgId]) || 0;
      sdsExpired = (props.sdsAlertCountWithOrg && props.sdsAlertCountWithOrg[orgId]) || 0;
      shipped = (props.shippedOrderWithOrg && props.shippedOrderWithOrg[orgId]) || 0;
      splitPending = (props.splitPendingOrderWithOrg && props.splitPendingOrderWithOrg[orgId]) || 0;
    } else {
      unread = props.unreadOrder !== undefined ? props.unreadOrder : 0;
      notShipped = props.notShippedOrder !== undefined ? props.notShippedOrder : 0;
      cancelPending = props.cancelPending || 0;
      returnPending = props.returnPending || 0;
      splitPending = props.splitPending || 0;
      sdsRequest = props.sdsWaitingCount !== undefined ? props.sdsWaitingCount : 0;
      sdsNormal = props.sdsNormalCount !== undefined ? props.sdsNormalCount : 0;
      sdsExpired = props.sdsAlertCount !== undefined ? props.sdsAlertCount : 0;
      shipped = props.shippedOrder !== undefined ? props.shippedOrder : 0;
    }

    return {
      unread: unread,
      notShipped: notShipped,
      shipped: shipped,
      cancelPending: cancelPending,
      returnPending: returnPending,
      splitPending: splitPending,
      sdsRequest: sdsRequest,
      sdsNormal: sdsNormal,
      sdsExpired: sdsExpired
    };
  };

  clearListeners = () => {
    this.unsubscribers.forEach((unsub) => {
      if (typeof unsub === 'function') {
        try { unsub(); } catch (e) { }
      }
    });
    this.unsubscribers = [];
  };

  // Helper matching OrderTableList.js & App.js to determine user role and partner org
  getOrgParams = () => {
    const { userOrg, org } = this.props;
    let toOrg = "";
    let fromOrg = "";
    let isSellerMode = false;
    let isBuyerMode = false;

    if (userOrg && userOrg.reseller && org && org.buyer) {
      isSellerMode = true;
      toOrg = userOrg.id;
      fromOrg = org.id;
    } else if (userOrg && userOrg.maker && org && org.reseller) {
      isSellerMode = true;
      toOrg = userOrg.id;
      fromOrg = org.id;
    } else if (userOrg && userOrg.reseller && org && org.maker) {
      isBuyerMode = true;
      toOrg = org.id;
      fromOrg = userOrg.id;
    } else if (userOrg && userOrg.buyer && org && org.reseller) {
      isBuyerMode = true;
      toOrg = org.id;
      fromOrg = userOrg.id;
    } else {
      toOrg = userOrg?.id || "";
      fromOrg = org?.id || "";
      if (userOrg?.reseller || userOrg?.maker) {
        isSellerMode = true;
      } else if (userOrg?.buyer) {
        isBuyerMode = true;
      }
    }

    const currentOrgId = userOrg?.id || org?.id || "";
    return { toOrg, fromOrg, isSellerMode, isBuyerMode, currentOrgId };
  };

  // High-performance consolidated listener updating counts in sync with Firestore
  setupRealtimeListeners = () => {
    this.clearListeners();
    const { db } = this.props;
    const { toOrg, fromOrg, isSellerMode, currentOrgId } = this.getOrgParams();

    if (!db || (fromOrg === "" && toOrg === "")) {
      return;
    }

    this.setState({ loading: true });

    try {
      const ordersCollection = db.collection('orders');
      const sdsCollection = db.collection('sds_management');

      // 1. Single consolidated listener for orders collection (applied == true)
      let baseOrdersQuery = ordersCollection.where("applied", "==", true);
      if (fromOrg && toOrg) {
        baseOrdersQuery = baseOrdersQuery
          .where("fromOrganization", "==", fromOrg)
          .where("toOrganization", "==", toOrg);
      } else if (currentOrgId) {
        if (isSellerMode) {
          baseOrdersQuery = baseOrdersQuery.where("toOrganization", "==", currentOrgId);
        } else {
          baseOrdersQuery = baseOrdersQuery.where("fromOrganization", "==", currentOrgId);
        }
      }

      const unsubOrders = baseOrdersQuery.onSnapshot(
        (snapshot) => {
          if (!snapshot) return;
          this.cachedOrdersDocs = snapshot.docs || [];
          // console.log('cacheOrdersDocs', this.cachedOrdersDocs);
          let unread = 0;
          let notShipped = 0;
          let shipped = 0;
          let cancelPending = 0;
          let returnPending = 0;
          let splitPending = 0;

          snapshot.docs.forEach((doc) => {
            const data = doc.data() || {};
            // Unread order matching App.js exactly: readSeller == false for seller, readBuyer == false for buyer
            const isUnread = isSellerMode ? (data.readSeller === false) : (data.readBuyer === false);

            if (isUnread) unread++;
            if (data.isShipped === false) notShipped++;
            if (data.isShipped === true) shipped++;

            if (data.cancelWait === true) {
              const items = data.cart?.items || [];
              const waitItems = items.filter(v => v.cancelConfirm === false && v.cancelReject === false);
              cancelPending += (waitItems.length > 0 ? waitItems.length : 1);
            }

            if (data.returnWait === true) {
              const items = data.cart?.items || [];
              const waitItems = items.filter(v => v.returnConfirm === false && v.returnReject === false);
              returnPending += (waitItems.length > 0 ? waitItems.length : 1);
            }

            const items = data.cart?.items || [];
            const hasSubDivideWait = items.some(v => v.subDivided && v.divideConfirm === false);
            if (data.divideConfirm === false || hasSubDivideWait) {
              splitPending++;
            }
          });

          this.setState((prev) => ({
            counts: {
              ...prev.counts,
              unread: unread || prev.counts.unread,
              notShipped: notShipped || prev.counts.notShipped,
              shipped: shipped || prev.counts.shipped,
              cancelPending: cancelPending || prev.counts.cancelPending,
              returnPending: returnPending || prev.counts.returnPending,
              splitPending: splitPending || prev.counts.splitPending
            },
            loading: false
          }));
        },
        (error) => {
          console.warn('Orders realtime listener warning:', error);
          this.setState({ loading: false });
        }
      );
      this.unsubscribers.push(unsubOrders);

      // 2. Single consolidated listener for SDS management (matching App.js & SDSManagementTableList)

      let baseSdsQuery = sdsCollection;
      let fromOrg1 = "";
      let toOrg1 = "";
      if (this.props.userOrg && this.props.userOrg.reseller && this.props.org && this.props.org.buyer) {
        fromOrg1 = this.props.userOrg?.id;
        toOrg1 = this.props.org.id;
      }
      if (this.props.userOrg && this.props.userOrg.maker && this.props.org && this.props.org.reseller) {
        fromOrg1 = this.props.userOrg?.id;
        toOrg1 = this.props.org.id;
      }
      if (this.props.userOrg && this.props.userOrg.reseller && this.props.org && this.props.org.maker) {
        fromOrg1 = this.props.org.id;
        toOrg1 = this.props.userOrg?.id;
      }
      if (this.props.userOrg && this.props.userOrg.buyer && this.props.org && this.props.org.reseller) {
        fromOrg1 = this.props.org.id;
        toOrg1 = this.props.userOrg?.id;
      }
      if (this.props.orgSeller && this.props.org && this.props.user && this.props.user.admin) {
        fromOrg1 = this.props.orgSeller.id;
        toOrg1 = this.props.org.id;
      }
      if (fromOrg1 && toOrg1) {
        baseSdsQuery = baseSdsQuery
          .where("fromOrganization", "==", fromOrg1)
          .where("toOrganization", "==", toOrg1)
          .where('item.archive', "==", false);
      } else if (currentOrgId) {
        if (isSellerMode) {
          baseSdsQuery = baseSdsQuery.where("toOrganization", "==", currentOrgId);
        } else {
          baseSdsQuery = baseSdsQuery.where("fromOrganization", "==", currentOrgId);
        }
      }
      console.log('baseSdsQuery', baseSdsQuery)
      const unsubSds = baseSdsQuery.onSnapshot(
        (snapshot) => {
          if (!snapshot) return;
          this.cachedSdsDocs = snapshot.docs || [];
          console.log('cachedSdsDocs12', this.cachedSdsDocs)
          let sdsRequest = 0;
          let sdsNormal = 0;
          let sdsExpired = 0;

          snapshot.docs.forEach((doc) => {
            const data = doc.data() || {};
            if (data.item?.archive === true) return;
            if (data.status === 'waiting') sdsRequest++;
            if (data.status === 'normal') sdsNormal++;
            if (data.status === 'alert' || data.status === 'warning' || data.status === 'expired') sdsExpired++;
          });

          this.setState((prev) => ({
            counts: {
              ...prev.counts,
              sdsRequest: sdsRequest || prev.counts.sdsRequest,
              sdsNormal: sdsNormal || prev.counts.sdsNormal,
              sdsExpired: sdsExpired || prev.counts.sdsExpired
            },
            loading: false
          }));
        },
        (error) => {
          console.warn('SDS realtime listener warning:', error);
          this.setState({ loading: false });
        }
      );
      this.unsubscribers.push(unsubSds);

    } catch (err) {
      console.error('Error setting up dashboard listeners:', err);
      this.setState({ loading: false });
    }
  };

  // Open modal with filtered order list based on selected status card
  handleCardClick = (category, statusText, statusKey, defaultCount) => {
    const { isSellerMode } = this.getOrgParams();
    let orders = [];

    if (statusKey.startsWith('sds')) {
      console.log('cachedSdsDocs', this.cachedSdsDocs);
      const filteredSdsDocs = this.cachedSdsDocs.filter((doc) => {

        const d = doc.data() || {};
        if (d.item?.archive === true) return false;
        if (statusKey === 'sdsRequest') return d.status === 'waiting';
        if (statusKey === 'sdsNormal') return d.status === 'normal';
        if (statusKey === 'sdsExpired') return d.status === 'alert' || d.status === 'warning' || d.status === 'expired';
        return false;
      });

      orders = filteredSdsDocs.map((doc) => {
        const d = doc.data() || {};
        return {
          id: doc.id,
          orderNo: d.managementNo || doc.id,
          partnerName: d.partnerName || 'TESTペイント株式会社',
          buyerName: d.buyer_company_name || 'TESTペイント株式会社 本社',
          sellerName: d.seller_company_name || 'TESTリセラー株式会社 本社',
          orderDate: d.updatedAt ? (d.updatedAt.toDate ? d.updatedAt.toDate().toLocaleString() : d.updatedAt) : '2026-09-08',
          statusText: statusText,
          categoryLabel: category,
          isSds: true,
          statusKey: statusKey,
          status: d.status,
          item: d.item,
          preferredDate: d.preferredDate,
          created: d.created,
          receptionDate: d.receptionDate,
          applicationUserName: d.applicationUserName,
          receptionUserName: d.receptionUserName,
          lastCheckDate: d.lastCheckDate,
          lastCheckUserName: d.lastCheckUserName,
          revisionDate: d.revisionDate,
          riskManagementStatus: d.riskManagementStatus,
          riskManagementApplyUserName: d.riskManagementApplyUserName,
          riskManagementReceptionUserName: d.riskManagementReceptionUserName,
          rawDoc: d,
          totalAmount: 0,
          notes: d.remarks || '',
          items: [{ code: 'SDS-DOC', name: d.item?.P4CLOUD商品名 || d.item?.name || 'SDS文書管理データ', spec: '電子ファイル', quantity: 1, unitPrice: 0 }]
        };
      });
    } else {
      const filteredOrderDocs = this.cachedOrdersDocs.filter((doc) => {
        const d = doc.data() || {};
        const readField = isSellerMode ? d.readSeller : d.readBuyer;

        if (statusKey === 'unread') return readField === false;
        if (statusKey === 'notShipped') return d.isShipped === false;
        if (statusKey === 'shipped') return d.isShipped === true;
        if (statusKey === 'cancelPending') return d.cancelWait === true;
        if (statusKey === 'returnPending') return d.returnWait === true;
        if (statusKey === 'splitPending') {
          const items = d.cart?.items || [];
          return d.divideConfirm === false || items.some(v => v.subDivided && v.divideConfirm === false);
        }
        return false;
      });

      orders = filteredOrderDocs.map((doc) => {
        const d = doc.data() || {};
        return {
          id: doc.id,
          orderNo: d.cart?.orderNo || d.orderNo || doc.id,
          partnerName: d.partnerName || d.seller_company_name || d.buyer_company_name || 'TESTペイント株式会社',
          buyerName: d.buyer_company_name || 'TESTペイント株式会社 本社',
          sellerName: d.seller_company_name || 'TESTリセラー株式会社 本社',
          orderDate: d.sortDate ? (d.sortDate.toDate ? d.sortDate.toDate().toLocaleString() : d.sortDate) : '2026-09-08',
          sortDate: d.sortDate,
          latestUpdate: d.latestUpdate,
          created: d.created,
          sellerConfirmDate: d.sellerConfirmDate,
          statusText: statusText,
          categoryLabel: category,
          statusKey: statusKey,
          totalAmount: d.cart?.total || d.totalAmount || 18500,
          notes: d.notes || '',
          cart: d.cart,
          items: d.cart?.items || d.cart?.item || d.items || [],
          rawDoc: d
        };
      });
    }

    const currentCount = this.state.counts[statusKey] !== undefined ? this.state.counts[statusKey] : defaultCount;



    this.setState({
      selectedCard: { category, statusText, statusKey, count: currentCount },
      filteredOrders: orders,
      listModalVisible: true
    });
  };

  handleSelectOrder = (order) => {
    if (order.isSds || (order.statusKey && order.statusKey.startsWith('sds'))) {
      window.open('/sdsManagement', '_blank');
    } else {
      const orderId = order.id || order.orderNo;
      window.open(`/order/${orderId}`, '_blank');
    }
  };

  render() {
    if (!this.props.org) {
      return (
        <>
          取引先を選択してください。
        </>
      )
    }
    const { loading, counts, listModalVisible, selectedCard, filteredOrders } = this.state;

    return (
      <div className="order-dashboard-container">
        {/* Breadcrumb Navigation */}
        {/* <Breadcrumb style={{ marginBottom: 16 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined /> Top Page
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <DashboardOutlined /> ダッシュボード
          </Breadcrumb.Item>
        </Breadcrumb> */}

        {/* Dashboard Title & Action Bar */}
        <Card className="dashboard-header-card" bodyStyle={{ padding: '20px 24px' }}>
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col xs={24} sm={14}>
              <Title level={3} className="dashboard-title">
                <DashboardOutlined style={{ color: '#1677ff' }} />
                ダッシュボード
              </Title>
              <Text type="secondary" style={{ marginTop: 4, display: 'block' }}>
                注文・出荷状況・キャンセル・返品・SDS管理のリアルタイム統計一覧
              </Text>
            </Col>
            {/* <Col xs={24} sm={10} style={{ textAlign: 'right' }}>
              <Space wrap>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  loading={loading}
                  onClick={this.setupRealtimeListeners}
                >
                  最新情報に更新
                </Button>
              </Space>
            </Col> */}
          </Row>
        </Card>

        <Spin spinning={loading} tip="データを読み込み中...">
          {/* Dashboard Status Cards Grid (3x3 matching reference image) */}
          <div style={{ margin: '0 -8px' }}>
            {/* ROW 1: 注文書 (未読 (未処理), 未出荷, 出荷済) */}
            <Row gutter={[16, 16]} className="dashboard-grid-row">
              <Col xs={24} sm={12} md={8}>
                <StatusCard
                  category="注文書"
                  subLabel="未読（未処理）"
                  count={counts.unread}
                  theme="blue"
                  icon={<FileTextOutlined style={{ color: '#1d39c4' }} />}
                  onClick={() => this.handleCardClick('注文書', '未読（未処理）', 'unread', counts.unread)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <StatusCard
                  category="注文書"
                  subLabel="未出荷"
                  count={counts.notShipped}
                  theme="orange"
                  icon={<ClockCircleOutlined style={{ color: '#d46b08' }} />}
                  onClick={() => this.handleCardClick('注文書', '未出荷', 'notShipped', counts.notShipped)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <StatusCard
                  category="注文書"
                  subLabel="出荷済"
                  count={counts.shipped}
                  theme="green"
                  icon={<CheckCircleOutlined style={{ color: '#389e0d' }} />}
                  onClick={() => this.handleCardClick('注文書', '出荷済', 'shipped', counts.shipped)}
                />
              </Col>
            </Row>

            {/* ROW 2: キャンセル, 返品, 分納 (承認待) */}
            <Row gutter={[16, 16]} className="dashboard-grid-row">
              <Col xs={24} sm={12} md={8}>
                <StatusCard
                  category="キャンセル"
                  subLabel="承認待"
                  count={counts.cancelPending}
                  theme="red"
                  icon={<ExclamationCircleOutlined style={{ color: '#cf1322' }} />}
                  onClick={() => this.handleCardClick('キャンセル', '承認待', 'cancelPending', counts.cancelPending)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <StatusCard
                  category="返品"
                  subLabel="承認待"
                  count={counts.returnPending}
                  theme="cyan"
                  icon={<RollbackOutlined style={{ color: '#08979c' }} />}
                  onClick={() => this.handleCardClick('返品', '承認待', 'returnPending', counts.returnPending)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <StatusCard
                  category="分納"
                  subLabel="承認待"
                  count={counts.splitPending}
                  theme="purple"
                  icon={<SplitCellsOutlined style={{ color: '#531dab' }} />}
                  onClick={() => this.handleCardClick('分納', '承認待', 'splitPending', counts.splitPending)}
                />
              </Col>
            </Row>

            {/* ROW 3: SDS管理 (申込依頼, 正常更新中, チェック更新期限切れ) */}
            <Row gutter={[16, 16]} className="dashboard-grid-row">
              <Col xs={24} sm={12} md={8}>
                <StatusCard
                  category="SDS管理"
                  subLabel="申込依頼"
                  count={counts.sdsRequest}
                  theme="cyan"
                  icon={<FileSearchOutlined style={{ color: '#08979c' }} />}
                  onClick={() => this.handleCardClick('SDS管理', '申込依頼', 'sdsRequest', counts.sdsRequest)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <StatusCard
                  category="SDS管理"
                  subLabel="正常更新中"
                  count={counts.sdsNormal}
                  theme="blue"
                  icon={<SafetyCertificateOutlined style={{ color: '#1d39c4' }} />}
                  onClick={() => this.handleCardClick('SDS管理', '正常更新中', 'sdsNormal', counts.sdsNormal)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <StatusCard
                  category="SDS管理"
                  subLabel="チェック更新期限切れ"
                  count={counts.sdsExpired}
                  theme="red"
                  icon={<WarningOutlined style={{ color: '#cf1322' }} />}
                  onClick={() => this.handleCardClick('SDS管理', 'チェック更新期限切れ', 'sdsExpired', counts.sdsExpired)}
                />
              </Col>
            </Row>
          </div>
        </Spin>

        {/* Filtered Order List Modal */}
        {selectedCard && (
          <OrderListModal
            visible={listModalVisible}
            onClose={() => this.setState({ listModalVisible: false })}
            categoryLabel={selectedCard.category}
            statusLabel={selectedCard.statusText}
            statusKey={selectedCard.statusKey}
            orders={filteredOrders}
            onSelectOrder={this.handleSelectOrder}
          />
        )}
      </div>
    );
  }
}

export default OrderDashboard;
